import { createElement } from "react";
import { frameSource } from "./guest";
import { BUTTON_PLACES } from "../../lib/extensions/manifest";

// Runs a community extension (src/lib/extensions/manifest.js): opens its sandbox (guest.js) and gives
// what it adds to the platform as a plugin of its own, so its techniques, tools and buttons appear where
// everyone's do. Nothing it asks for is taken on trust: each request is checked here against the
// permissions it was given. If it stops answering or keeps failing, it's closed for this page.
const CALL_MS = 5000, MOST_ADDED = 40, MOST_FAILURES = 3, BIGGEST = 2 * 1024 * 1024;
const requires = { "score.get": "score.read", "score.set": "score.write", "storage.get": "storage", "storage.set": "storage" };
export const SHOW_PANEL = "truechops:extension-panel";

function ExtensionButton({ label, onPress, className }) {
  return createElement("button", { type: "button", className: className || "tc-extension-button", onClick: onPress, "data-extension-button": label }, label);
}

// `bridge`: how the page answers what an extension may ask ({ "score.get", "score.set", "storage.get",
// "storage.set", toast }). `say({ level, text })` hears its logs and failures.
export function startExtension(site, pack, { bridge, say = () => {} }) {
  const plugin = `ext:${pack.id}`, frame = document.createElement("iframe"), calls = new Map(), panels = new Map(), debounce = new Map();
  let api = null, nextCall = 1, failures = 0, added = 0, stopped = false, stopHearing = [];
  site.remove(plugin);
  site.use({ id: plugin, name: pack.name, setup(given) { api = given; } });
  const scoped = (id) => `${pack.id}.${id}`;
  const post = (message) => { if (!stopped) frame.contentWindow?.postMessage(message, "*"); };
  const stop = () => {
    if (stopped) return;
    stopped = true;
    window.removeEventListener("message", listen);
    stopHearing.forEach((off) => off());
    for (const { reject, timer } of calls.values()) { clearTimeout(timer); reject(new Error("The extension was closed.")); }
    frame.remove();
    site.remove(plugin);
  };
  const failed = (text) => { say({ level: "error", text }); failures += 1; if (failures >= MOST_FAILURES) { say({ level: "error", text: `${pack.name} kept failing, so it's been closed for this page.` }); stop(); } };
  // Calling something it added. No answer in time closes it: it may be stuck.
  const call = (name, input) => new Promise((resolve, reject) => {
    if (stopped) { reject(new Error(`${pack.name} isn't running.`)); return; }
    const id = nextCall++, timer = setTimeout(() => { calls.delete(id); reject(new Error(`${pack.name} took too long and was closed.`)); say({ level: "error", text: `${pack.name} took too long and was closed.` }); stop(); }, CALL_MS);
    calls.set(id, { resolve, reject, timer });
    post({ type: "call", id, name, input: JSON.parse(JSON.stringify(input ?? null)) });
  });
  const reply = (id, value) => post({ type: "answer", id, ok: true, value });
  const refuse = (id, error) => post({ type: "answer", id, ok: false, error });

  async function answer({ id, method, args = [] }) {
    const needs = requires[method];
    if (needs && !pack.permissions.includes(needs)) return refuse(id, `This extension needs the ${needs} permission for that.`);
    try {
      if (method === "panel.post") { panels.get(args[0])?.contentWindow?.postMessage(args[1], "*"); return reply(id, null); }
      if (!Object.hasOwn(bridge, method)) return refuse(id, `There's no ${method}.`);
      reply(id, await bridge[method](pack, ...args));
    } catch (error) { refuse(id, error.message || "That didn't work."); }
  }

  function add({ what, definition: given }) {
    if (added++ >= MOST_ADDED) return failed(`${pack.name} tried to add too much.`);
    const id = String(given?.id || ""), name = String(given?.name || given?.label || id).slice(0, 60);
    if (!/^[a-z][\w-]{0,40}$/i.test(id) && what !== "button") return failed(`Each thing an extension adds needs an id like "my-tool" (got "${id}").`);
    if (what === "technique") api.technique({ id: scoped(id), name, does: String(given.does || "").slice(0, 200), kinds: Array.isArray(given.kinds) ? given.kinds : undefined, icon: given.icon, run: (input) => call(`technique:${id}`, input) });
    else if (what === "clipTool") api.clipTool({ id: scoped(id), name, run: (input) => call(`clipTool:${id}`, input) });
    else if (what === "audioEffect") api.audioEffect({ id: scoped(id), name, group: pack.name, knobs: given.knobs, code: String(given.code || "") });
    else if (what === "button" || what === "panel") {
      const place = BUTTON_PLACES.includes(given.place) ? given.place : null;
      if (!place) return failed(`A button's place must be one of ${BUTTON_PLACES.join(", ")}.`);
      const open = what === "panel" ? () => window.dispatchEvent(new CustomEvent(SHOW_PANEL, { detail: { pack, id, title: name, html: String(given.html || ""), attach: (panelFrame) => panels.set(id, panelFrame), send: (data) => call(`panel:${id}`, data) } })) : () => call(`button:${id}`, null).catch((error) => failed(error.message));
      api.slot(`${place}.end`, ExtensionButton, { props: { label: String(given.label || name).slice(0, 30), onPress: open } });
    }
  }
  // What it listens for, a little behind so a run of changes is one.
  function listenFor(event) {
    if (event === "score.changed" && !pack.permissions.includes("score.read")) return say({ level: "warn", text: "score.changed needs the score.read permission." });
    if (!["score.changed", "route.changed"].includes(event) || debounce.has(event)) return;
    debounce.set(event, null);
    stopHearing.push(api.on(event, (payload) => {
      clearTimeout(debounce.get(event));
      debounce.set(event, setTimeout(() => post({ type: "event", event, payload: JSON.parse(JSON.stringify(event === "score.changed" ? { score: payload.score, bars: payload.bars } : payload)) }), 300));
    }));
  }
  function listen(event) {
    if (event.source !== frame.contentWindow || stopped) return;
    const data = event.data;
    if (!data || typeof data !== "object") return;
    if (JSON.stringify(data).length > BIGGEST) return failed(`${pack.name} sent too much at once.`);
    if (data.type === "add") add(data);
    else if (data.type === "ask") answer(data);
    else if (data.type === "listen") listenFor(data.event);
    else if (data.type === "log") say({ level: data.level, text: data.text });
    else if (data.type === "failed") { say({ level: "error", text: `${pack.name} couldn't start: ${data.error}` }); stop(); }
    else if (data.type === "result") {
      const waiting = calls.get(data.id);
      if (!waiting) return;
      calls.delete(data.id); clearTimeout(waiting.timer);
      if (data.ok) waiting.resolve(data.value); else { waiting.reject(new Error(data.error || "It failed.")); failed(data.error || "It failed."); }
    }
  }
  window.addEventListener("message", listen);
  frame.setAttribute("sandbox", "allow-scripts");
  frame.setAttribute("aria-hidden", "true");
  frame.setAttribute("data-extension", pack.id);
  frame.style.cssText = "position:absolute;width:0;height:0;border:0;visibility:hidden";
  frame.srcdoc = frameSource(pack.code, pack.hosts || []);
  document.body.appendChild(frame);
  return { id: pack.id, stop, call };
}
